import React, { useEffect, useState } from "react";
import { Index, InfiniteLoader } from "react-virtualized";

import { bemClassNames, DEFAULT_DATA_COUNT_TO_LOAD, doNothing, useForceUpdate } from "@multiweb/reactUtils";
import { FetchingState } from "@multiweb/types/react";
import { DataGridVariant } from "../types";
import { VirtualizedList, VirtualizedListProps } from "../VirtualizedList";

export type InfiniteVirtualizedListProps<T> = VirtualizedListProps<T> & {
    /** Počet dat k dočtení. Slouží pro vytvoření patřičného počtu skeletonů. */
    dataCountToLoad?: number;
    /** Určuje, zda je možné načítat další data. */
    canLoadNextItems: boolean;
    /** Uchovává informaci, v jakém stavu je načítání dat. */
    fetchingState: FetchingState;
    /** Upravuje výchozí chování vzhledu VirtualizedListu. */
    variant?: DataGridVariant;
    /** Odhad výšky záznamu. */
    estimatedRowSize?: number;
    /** Funkce, která slouží pro načítání dalších řádků s daty. */
    loadNextItems: (count: number) => void;
    /** Funkce, která vrací pole skeletonů. */
    createSkeletonRowsKeys: (count: number) => ReadonlyArray<T>;
};

const asyncEmpty = new Promise(doNothing);

const classNames = bemClassNames("infinite-virtualized-list-component");

export const InfiniteVirtualizedList = <T,>({
    items,
    keyGenerator,
    itemRenderer,
    cellMeasureCacheParams,
    canLoadNextItems,
    loadNextItems,
    dataCountToLoad = DEFAULT_DATA_COUNT_TO_LOAD,
    fetchingState,
    estimatedRowSize,
    scrollToIndex,
    variant = DataGridVariant.default,
    onScroll,
    stickyHeaderCreator,
    stickyFooterCreator,
    noRowsRenderer,
    createSkeletonRowsKeys,
}: InfiniteVirtualizedListProps<T>) => {
    const [resolveLoadingPromise, setResolveLoadingPromise] = useState<(() => void) | null>();
    const [rejectLoadingPromise, setRejectLoadingPromise] = useState<(() => void) | null>();
    const forceUpdate = useForceUpdate();
    useEffect(() => {
        if (fetchingState === FetchingState.success && resolveLoadingPromise) {
            resolveLoadingPromise();
        } else if (fetchingState === FetchingState.error && rejectLoadingPromise) {
            rejectLoadingPromise();
        }
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchingState]);

    useEffect(() => {
        forceUpdate();
    }, [forceUpdate, itemRenderer]);

    const displaySkeletons = canLoadNextItems
        || fetchingState === FetchingState.initialized
        || fetchingState === FetchingState.request;

    const ivlItems = displaySkeletons ? items.concat(createSkeletonRowsKeys(dataCountToLoad)) : items;
    const rowCount = ivlItems.length;

    const isRowLoaded = ({ index }: Index) => {
        return index < items.length ? !!items[index] : false;
    };

    const asyncLoadNextItems = () =>
        new Promise<void>((resolve, reject) => {
            loadNextItems(dataCountToLoad);
            setResolveLoadingPromise(() => {
                resolve();
                setResolveLoadingPromise(null);
                setRejectLoadingPromise(null);
            });
            setRejectLoadingPromise(() => {
                reject();
                setResolveLoadingPromise(null);
                setRejectLoadingPromise(null);
            });
        });

    const loadMoreRows =
        fetchingState === FetchingState.request || !canLoadNextItems ? () => asyncEmpty : asyncLoadNextItems;

    return (
        <div className={classNames({ variant })}>
            <InfiniteLoader {...{ isRowLoaded, loadMoreRows, rowCount }}>
                {({ onRowsRendered, registerChild }) => (
                    <VirtualizedList
                        ref={registerChild}
                        items={ivlItems}
                        {...{
                            cellMeasureCacheParams,
                            itemRenderer,
                            keyGenerator,
                            onRowsRendered,
                            stickyHeaderCreator,
                            stickyFooterCreator,
                            noRowsRenderer,
                            variant,
                            estimatedRowSize,
                            onScroll,
                            scrollToIndex,
                        }}
                    />
                )}
            </InfiniteLoader>
        </div>
    );
};
